import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Loader from "examples/Loader";
import {
  useFreeAccountTemplates,
  useDeleteFreeAccountTemplate,
  freeAccountTemplateKeys,
} from "queries/accounts";
import { useAgencyInfo } from "context/agency";
import { ModalComponent as CreateModelTemplate } from "../CreateModelTemplate";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

function FreeAccountTemplate() {
  const [agency] = useAgencyInfo();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [openCreate, setOpenCreate] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const itemsPerPage = 10;
  const maxButtonsToShow = 5;

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchQuery), 1000);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // A fresh search resets back to page 1, same as the old debounced effect did.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const refreshList = () => queryClient.invalidateQueries({ queryKey: freeAccountTemplateKeys.all });

  const { data, isFetching } = useFreeAccountTemplates({
    agencyId: agency._id,
    page: currentPage,
    search: debouncedSearch,
    limit: itemsPerPage,
  });

  const { mutateAsync: deleteTemplate } = useDeleteFreeAccountTemplate();

  const handleEdit = (item) => {
    setEditTemplate(item);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete template "${item.template_name}"?`)) return;
    await deleteTemplate(item._id);
  };

  const loader = isFetching;
  const totalPages = data?.totalpages || 1;

  const startPage = useMemo(
    () => Math.max(currentPage - Math.floor(maxButtonsToShow / 2), 1),
    [currentPage]
  );
  const endPage = useMemo(
    () => Math.min(startPage + maxButtonsToShow - 1, totalPages),
    [startPage, totalPages]
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber === currentPage) return;
    setCurrentPage(pageNumber);
  };

  const handlePreviousPage = () => currentPage > 1 && handlePageChange(currentPage - 1);
  const handleNextPage = () => currentPage < totalPages && handlePageChange(currentPage + 1);

  const currentItems = Array.isArray(data?.data) ? data.data : [];

  return (
    <div className="free-account-template-page">
      {/* Search Input — same as Cancelled Accounts (NewTable) */}
      <div
        style={{
          position: "relative",
          marginBottom: "20px",
          display: "flex",
          width: "30%",
          float: "right",
        }}
      >
        <div style={{ position: "absolute", left: "10px", top: "20px" }}>
          <svg
            width="25"
            height="25"
            viewBox="0 0 15 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_282_92)">
              <path
                d="M15 13.2955L12.3767 10.7727C13.1166 9.68182 13.5202 8.31818 13.5202 6.88636C13.5202 3.06818 10.4933 0 6.79372 0C3.09417 0 0 3.06818 0 6.88636C0 10.7045 3.02691 13.7727 6.79372 13.7727C8.27354 13.7727 9.61883 13.2955 10.6951 12.4773L13.3184 15.0682L15 13.2955ZM2.35426 6.88636C2.35426 4.43182 4.30493 2.45455 6.72646 2.45455C9.14798 2.45455 11.0987 4.43182 11.0987 6.88636C11.0987 9.34091 9.14798 11.3182 6.72646 11.3182C4.3722 11.3182 2.35426 9.34091 2.35426 6.88636Z"
                fill="#E5E5E5"
              />
            </g>
            <defs>
              <clipPath id="clip0_282_92">
                <rect width="15" height="15" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </div>
        <input
          type="text"
          placeholder="Search by  name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            padding: "30px 45px",
            width: "100%",
            fontSize: "18px",
            boxSizing: "border-box",
            border: "1px solid #ccc",
            borderRadius: "5px",
          }}
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "16px",
          clear: "both",
        }}
      >
        <button
          onClick={() => setOpenCreate(true)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "12px 22px",
            backgroundColor: "black",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "16px",
            cursor: "pointer",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-plus h-4 w-4"
            aria-hidden="true"
          >
            <path d="M5 12h14"></path>
            <path d="M12 5v14"></path>
          </svg>
          New Template
        </button>
      </div>

      {/* Table */}
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "10px",
          border: "1px solid #E5E5E5",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            backgroundColor: "#F9FAFB",
            padding: "16px 24px",
            fontWeight: 600,
            fontSize: "14px",
            color: "#111827",
            borderBottom: "1px solid #E5E5E5",
          }}
        >
          <div style={{ flex: 2 }}>Template Name</div>
          <div style={{ flex: 1 }}>AI Task tokens</div>
          <div style={{ flex: 1 }}>AI Agents</div>
          <div style={{ flex: 1 }}>Team Members</div>
          <div style={{ flex: 1 }}>Workspaces</div>
          <div style={{ flex: 1, textAlign: "right" }}>Actions</div>
        </div>

        {loader ? (
          <div style={{ padding: "40px 0" }}>
            <Loader />
          </div>
        ) : currentItems.length > 0 ? (
          currentItems.map((item, index) => (
            <div
              key={item._id || index}
              style={{
                display: "flex",
                padding: "18px 24px",
                fontSize: "14px",
                color: "#111827",
                borderBottom:
                  index === currentItems.length - 1 ? "none" : "1px solid #F1F5F9",
                alignItems: "center",
              }}
            >
              <div style={{ flex: 2, fontWeight: 600 }}>
                {item.template_name || "—"}
              </div>
              <div style={{ flex: 1 }}>
                {item.unlimited_tasks ? "Unlimited" : item.number_of_tokens || "—"}
              </div>
              <div style={{ flex: 1 }}>
                {item.can_build_custom_ai_agents
                  ? item.unlimited_ai_agents
                    ? "Unlimited"
                    : item.number_of_ai_agents || "—"
                  : "—"}
              </div>
              <div style={{ flex: 1 }}>
                {item.can_add_team_members ? item.number_of_team_member || "—" : "—"}
              </div>
              <div style={{ flex: 1 }}>
                {item.create_additional_workspaces
                  ? item.number_of_additional_workspaces || "—"
                  : "—"}
              </div>
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "8px",
                }}
              >
                <button
                  type="button"
                  onClick={() => handleEdit(item)}
                  title="Edit template"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "32px",
                    height: "32px",
                    background: "white",
                    border: "1px solid #E5E5E5",
                    borderRadius: "6px",
                    cursor: "pointer",
                    color: "#111827",
                  }}
                >
                  <EditIcon style={{ fontSize: "18px" }} />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(item)}
                  title="Delete template"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "32px",
                    height: "32px",
                    background: "red",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    color: "white",
                  }}
                >
                  <DeleteIcon style={{ fontSize: "18px" }} />
                </button>
              </div>
            </div>
          ))
        ) : (
          <p style={{ textAlign: "center", padding: "40px 0", color: "#6B7280" }}>
            No templates found
          </p>
        )}
      </div>

      {/* Pagination */}
      {!loader && currentItems.length > 0 && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            style={{
              padding: "8px 16px",
              margin: "0 5px",
              backgroundColor: "white",
              color: "black",
              border: "1px solid black",
              borderRadius: "4px",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
              opacity: currentPage === 1 ? 0.6 : 1,
            }}
          >
            Previous
          </button>

          {[...Array(endPage - startPage + 1)].map((_, index) => {
            const pageNumber = startPage + index;
            return (
              <button
                key={pageNumber}
                onClick={() => handlePageChange(pageNumber)}
                style={{
                  padding: "8px 16px",
                  margin: "0 5px",
                  backgroundColor: pageNumber === currentPage ? "black" : "white",
                  color: pageNumber === currentPage ? "white" : "black",
                  border:
                    pageNumber === currentPage ? "1px solid white" : "1px solid black",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                {pageNumber}
              </button>
            );
          })}

          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            style={{
              padding: "8px 16px",
              margin: "0 5px",
              backgroundColor: "white",
              color: "black",
              border: "1px solid black",
              borderRadius: "4px",
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
              opacity: currentPage === totalPages ? 0.6 : 1,
            }}
          >
            Next
          </button>
        </div>
      )}

      <CreateModelTemplate
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        handleRefresh={refreshList}
        title="Create New Template"
      />

      <CreateModelTemplate
        open={!!editTemplate}
        onClose={() => setEditTemplate(null)}
        handleRefresh={refreshList}
        title="Edit Template"
        templateData={editTemplate}
      />
    </div>
  );
}

export default FreeAccountTemplate;
